import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ChevronRight, 
  ChevronLeft,
  Heart, 
  Brain, 
  Stethoscope, 
  Wind, 
  Activity, 
  Pill,
  Apple,
  Sparkles,
  X,
  Minimize2,
  Maximize2
} from 'lucide-react';
import axios from 'axios';
import { ScrollArea } from './ui/scroll-area';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icons for therapeutic areas
const TA_ICONS = {
  autoimmune: Activity,
  dermatology: Sparkles,
  cardiology: Heart,
  pulmonary: Wind,
  neurology: Brain,
  oncology: Pill,
  endocrinology: Apple,
  gastroenterology: Stethoscope,
};

// Colors for therapeutic areas
const TA_COLORS = {
  autoimmune: '#FF6B6B',
  dermatology: '#4ECDC4',
  cardiology: '#FF3B30',
  pulmonary: '#0066FF',
  neurology: '#7000FF',
  oncology: '#FF9500',
  endocrinology: '#34C759',
  gastroenterology: '#AF52DE',
};

const PatientNavigator = ({ onContextChange, currentContext }) => {
  const [therapeuticAreas, setTherapeuticAreas] = useState({});
  const [contentCategories, setContentCategories] = useState({});
  const [selectedTA, setSelectedTA] = useState(currentContext?.therapeuticArea || null);
  const [selectedDisease, setSelectedDisease] = useState(currentContext?.disease || null);
  const [selectedCategory, setSelectedCategory] = useState(currentContext?.contentCategory || null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    fetchTherapeuticAreas();
  }, []);

  const fetchTherapeuticAreas = async () => {
    try {
      const response = await axios.get(`${API}/therapeutic-areas`);
      setTherapeuticAreas(response.data.therapeutic_areas);
      setContentCategories(response.data.content_categories);
    } catch (error) {
      console.error('Error fetching therapeutic areas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTASelect = (taKey) => {
    setSelectedTA(taKey);
    setSelectedDisease(null);
    setSelectedCategory(null);
    onContextChange({ therapeuticArea: taKey, disease: null, contentCategory: null });
  };

  const handleDiseaseSelect = (disease) => {
    setSelectedDisease(disease);
    setSelectedCategory(null);
    onContextChange({ therapeuticArea: selectedTA, disease, contentCategory: null });
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    onContextChange({ therapeuticArea: selectedTA, disease: selectedDisease, contentCategory: category });
  };

  const goBack = () => {
    if (selectedCategory) {
      setSelectedCategory(null);
      onContextChange({ therapeuticArea: selectedTA, disease: selectedDisease, contentCategory: null });
    } else if (selectedDisease) {
      setSelectedDisease(null);
      setSelectedCategory(null);
      onContextChange({ therapeuticArea: selectedTA, disease: null, contentCategory: null });
    } else if (selectedTA) {
      setSelectedTA(null);
      setSelectedDisease(null);
      setSelectedCategory(null);
      onContextChange({ therapeuticArea: null, disease: null, contentCategory: null });
    }
  };

  const clearSelection = () => {
    setSelectedTA(null);
    setSelectedDisease(null);
    setSelectedCategory(null);
    onContextChange({ therapeuticArea: null, disease: null, contentCategory: null });
  };

  if (isLoading) {
    return (
      <motion.div
        className="fixed left-6 top-24 z-40 w-72 bg-white/90 backdrop-blur-xl rounded-2xl border border-gray-200 shadow-xl p-4"
        initial={{ x: -300, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
      >
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
        </div>
      </motion.div>
    );
  }

  // Collapsed state
  if (isCollapsed) {
    return (
      <motion.button
        className="fixed left-6 top-24 z-40 bg-[#CCFF00] text-black rounded-full p-3 shadow-xl hover:bg-black hover:text-white transition-colors"
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        onClick={() => setIsCollapsed(false)}
        data-testid="patient-navigator-expand"
      >
        <Maximize2 className="w-5 h-5" />
      </motion.button>
    );
  }

  return (
    <motion.div
      className="fixed left-6 top-24 z-40 w-80 bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200 shadow-xl overflow-hidden"
      initial={{ x: -300, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: -300, opacity: 0 }}
      data-testid="patient-navigator"
    >
      {/* Header */}
      <div className="bg-[#CCFF00] p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-heading font-bold text-sm">PATIENT CONTENT</h3>
            <p className="text-xs text-black/60 mt-1">Health & Wellness Topics</p>
          </div>
          <div className="flex items-center gap-1">
            <button 
              onClick={() => setIsCollapsed(true)}
              className="p-1.5 hover:bg-black/10 rounded-full transition-colors"
              title="Minimize"
            >
              <Minimize2 className="w-4 h-4" />
            </button>
            {selectedTA && (
              <button 
                onClick={clearSelection}
                className="p-1.5 hover:bg-black/10 rounded-full transition-colors"
                title="Clear selection"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Back Button & Breadcrumb */}
      {selectedTA && (
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <button 
              onClick={goBack}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1 text-xs text-gray-600 flex-wrap flex-1">
              <span 
                className="cursor-pointer hover:text-black font-medium"
                onClick={() => {
                  setSelectedTA(null);
                  setSelectedDisease(null);
                  setSelectedCategory(null);
                  onContextChange({ therapeuticArea: null, disease: null, contentCategory: null });
                }}
              >
                All Areas
              </span>
              {selectedTA && (
                <>
                  <ChevronRight className="w-3 h-3" />
                  <span 
                    className={`cursor-pointer hover:text-black ${!selectedDisease ? 'font-medium text-black' : ''}`}
                    onClick={() => handleTASelect(selectedTA)}
                  >
                    {therapeuticAreas[selectedTA]?.name}
                  </span>
                </>
              )}
              {selectedDisease && (
                <>
                  <ChevronRight className="w-3 h-3" />
                  <span 
                    className={`cursor-pointer hover:text-black ${!selectedCategory ? 'font-medium text-black' : ''}`}
                    onClick={() => handleDiseaseSelect(selectedDisease)}
                  >
                    {selectedDisease}
                  </span>
                </>
              )}
              {selectedCategory && (
                <>
                  <ChevronRight className="w-3 h-3" />
                  <span className="font-medium text-[#7000FF]">
                    {contentCategories[selectedCategory]}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <ScrollArea className="h-[350px]">
        <div className="p-4">
          {/* Therapeutic Areas */}
          {!selectedTA && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 font-medium mb-3">SELECT THERAPEUTIC AREA</p>
              {Object.entries(therapeuticAreas).map(([key, ta]) => {
                const Icon = TA_ICONS[key] || Activity;
                const color = TA_COLORS[key] || '#000';
                return (
                  <motion.button
                    key={key}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-black hover:bg-gray-50 transition-all text-left"
                    onClick={() => handleTASelect(key)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid={`ta-${key}`}
                  >
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: `${color}20` }}
                    >
                      <Icon className="w-5 h-5" style={{ color }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{ta.name}</p>
                      <p className="text-xs text-gray-500">{ta.diseases?.length || 0} conditions</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </motion.button>
                );
              })}
            </div>
          )}

          {/* Diseases */}
          {selectedTA && !selectedDisease && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 font-medium mb-3">SELECT CONDITION</p>
              {therapeuticAreas[selectedTA]?.diseases?.map((disease, index) => (
                <motion.button
                  key={index}
                  className="w-full flex items-center justify-between p-3 rounded-xl border border-gray-200 hover:border-[#CCFF00] hover:bg-[#CCFF00]/10 transition-all text-left"
                  onClick={() => handleDiseaseSelect(disease)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`disease-${index}`}
                >
                  <span className="font-medium text-sm">{disease}</span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </motion.button>
              ))}
            </div>
          )}

          {/* Content Categories */}
          {selectedTA && selectedDisease && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 font-medium mb-3">SELECT CONTENT FOCUS (Optional)</p>
              {Object.entries(contentCategories).map(([key, label]) => (
                <motion.button
                  key={key}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all text-left ${
                    selectedCategory === key 
                      ? 'border-[#7000FF] bg-[#7000FF]/10 text-[#7000FF]'
                      : 'border-gray-200 hover:border-[#7000FF] hover:bg-[#7000FF]/5'
                  }`}
                  onClick={() => handleCategorySelect(key)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid={`category-${key}`}
                >
                  <span className="font-medium text-sm">{label}</span>
                  {selectedCategory === key && (
                    <span className="text-xs bg-[#7000FF] text-white px-2 py-0.5 rounded-full">
                      Selected
                    </span>
                  )}
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Footer hint */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          {!selectedTA && "Choose a therapeutic area to start"}
          {selectedTA && !selectedDisease && "Select a condition for targeted content"}
          {selectedDisease && !selectedCategory && "Pick a focus (optional) → select content type"}
          {selectedCategory && "Now select a content type from the mindmap →"}
        </p>
      </div>
    </motion.div>
  );
};

export default PatientNavigator;
